import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import {
  CCard,
  CCardBody,
  CButton,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
} from "@coreui/react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";

const PaymentMethodCard = ({
  paymentMethodData,
  setPaymentMethodData,
  dateRange,
  setDateRange,
  paymentMethod,
  setPaymentMethod,
  paymentOutcomeData,
  locked,
  onUnlock,
}) => {
  const chartRef = useRef(null);
  const outcomeChartRef = useRef(null);
  const dropdownRef = useRef(null);
  const currentMonth = new Date().toISOString().slice(0, 7);
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5);
  const startMonth = sixMonthsAgo.toISOString().slice(0, 7);

  const [startMonthState, setStartMonthState] = useState(startMonth);
  const [currentMonthState, setCurrentMonthState] = useState(currentMonth);
  const [hiddenItems, setHiddenItems] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [activeButton, setActiveButton] = useState(null);

  const handleDateChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      setTempDateRange(selectedDates);
      setActiveButton(null);
    }
    // Prevent dropdown from closing
    setDropdownVisible(true);
  };

  const applyDateRange = () => {
    setDateRange(tempDateRange);
    setDropdownVisible(false);
  };

  const handlePillClick = (method) => {
    setPaymentMethod(method);
    // setPaymentMethodData({
    //   labels: [''],
    //   datasets: [
    //     {
    //       backgroundColor: [''],
    //       data: [],
    //       label: 'Total Amount',
    //     },
    //   ],
    // });
  };

  const toggleItemVisibility = (label) => {
    setHiddenItems((prevHiddenItems) =>
      prevHiddenItems.includes(label)
        ? prevHiddenItems.filter((item) => item !== label)
        : [...prevHiddenItems, label]
    );
  };

  const setDateRangeToToday = () => {
    const today = new Date();
    setTempDateRange([today, today]);
  };

  const setDateRangeToThisWeek = () => {
    const today = new Date();
    const firstDayOfWeek = new Date(
      today.setDate(today.getDate() - today.getDay())
    );
    const lastDayOfWeek = new Date(
      today.setDate(today.getDate() - today.getDay() + 6)
    );
    setTempDateRange([firstDayOfWeek, lastDayOfWeek]);
  };

  const setDateRangeToThisMonth = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );
    setTempDateRange([firstDayOfMonth, lastDayOfMonth]);
  };

  const resetDateRange = () => {
    const start = new Date();
    start.setMonth(start.getMonth() - 5);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);

    const end = new Date();
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);

    setTempDateRange([start, end]);
  };

  const handleButtonClick = (rangeSetter, buttonId) => {
    rangeSetter();
    setActiveButton(buttonId);
  };

  const formatDateRange = (range) => {
    if (range.length === 2) {
      return `${range[0]
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-")} - ${range[1]
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-")}`.toUpperCase();
    }
    return "Select Date Range";
  };

  useEffect(() => {
    if (
      // Locked = the chart div isn't rendered (amounts are password-gated).
      locked ||
      !chartRef.current ||
      !paymentMethodData ||
      !paymentMethodData.datasets ||
      !paymentMethodData.labels ||
      !paymentMethodData.datasets[0]
    ) {
      return;
    }
    const chart = echarts.init(chartRef.current);
    const seriesData = paymentMethodData.datasets[0].data;
    const totalLabel = "Total";
    const formatNumber = (num) => num.toLocaleString();

    const filteredLabels = paymentMethodData.labels.filter(
      (label) => !hiddenItems.includes(label)
    );
    const filteredData = paymentMethodData.datasets[0].data.filter(
      (_, index) => !hiddenItems.includes(paymentMethodData.labels[index])
    );
    const filteredColors = paymentMethodData.datasets[0].backgroundColor.filter(
      (_, index) => !hiddenItems.includes(paymentMethodData.labels[index])
    );

    const option =
      paymentMethod === 1
        ? {
            tooltip: {
              trigger: "item",
            },
            grid: {
              bottom: "5%",
            },
            legend: {
              bottom: "0%",
              width: "400",
              left: "center",
              align: "auto",
              padding: [0, 100],
              selectedMode: "multiple",
              selected: paymentMethodData.labels.reduce((acc, label) => {
                acc[label] = true;
                return acc;
              }, {}),
            },
            series: [
              {
                name: "Transactions",
                type: "pie",
                radius: ["40%", "70%"],
                avoidLabelOverlap: false,
                itemStyle: {
                  borderRadius: 0,
                  borderColor: "#fff",
                  borderWidth: 0,
                },
                label: {
                  show: false,
                  position: "center",
                },
                emphasis: {
                  show: false,
                  textStyle: {
                    fontSize: "30",
                    fontWeight: "bold",
                  },
                },
                labelLine: {
                  show: false,
                },
                data: filteredLabels.map((label, index) => ({
                  value: filteredData[index],
                  name: label,
                  itemStyle: {
                    color: filteredColors[index],
                  },
                })),
              },
            ],
            graphic: {
              type: "group",
              left: "center",
              top: "center",
              children: [
                {
                  type: "text",
                  top: 0,
                  style: {
                    text: `${formatNumber(
                      filteredData.reduce((a, b) => a + b, 0)
                    )}`,
                    textAlign: "center",
                    fill: "#252B36F2",
                    fontSize: 20,
                    fontWeight: "bolder",
                  },
                },
                {
                  type: "text",
                  top: 30,
                  style: {
                    text: totalLabel,
                    textAlign: "center",
                    fill: "#252B36F2",
                    fontSize: 14,
                    fontWeight: "normal",
                  },
                },
              ],
            },
          }
        : {
            tooltip: {
              trigger: "axis",
              axisPointer: {
                type: "shadow",
              },
            },
            grid: {
              bottom: "5%",
            },
            xAxis: {
              type: "category",
              data: filteredLabels,
              axisLabel: {
                show: false,
              },
            },
            yAxis: {
              type: "value",
            },
            series: [
              {
                name: "Transactions",
                type: "bar",
                data: filteredData,
                itemStyle: {
                  color: (params) => filteredColors[params.dataIndex],
                },
              },
            ],
          };

    chart.setOption(option);

    if (paymentMethod === 1) {
      chart.on("legendselectchanged", function (params) {
        const selected = params.selected;
        const newTotal = filteredData.reduce((sum, value, index) => {
          return selected[filteredLabels[index]] ? sum + value : sum;
        }, 0);
        chart.setOption({
          graphic: {
            children: [
              {
                type: "text",
                top: 0,
                style: {
                  text: `${formatNumber(newTotal)}`,
                  textAlign: "center",
                  fill: "#252B36F2",
                  fontSize: 20,
                  fontWeight: "bolder",
                },
              },
            ],
          },
        });
      });
    }

    return () => {
      chart.dispose();
    };
  }, [paymentMethodData, paymentMethod, hiddenItems, locked]);

  // Success vs failed transaction counts per payment method group (ACS
  // Dashboard BRD, 3 Jul 2026).
  useEffect(() => {
    if (locked || !paymentOutcomeData || !outcomeChartRef.current) {
      return;
    }
    const chart = echarts.init(outcomeChartRef.current);

    const groups = [
      ["creditCard", "Credit Card"],
      ["onlineBanking", "Online Banking"],
      ["eWallet", "e-Wallet"],
      ["other", "Other"],
    ];
    const bucketFor = (key) =>
      paymentOutcomeData[key] || { success_count: 0, failed_count: 0 };

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      grid: {
        bottom: "15%",
      },
      legend: {
        bottom: "0%",
        left: "center",
      },
      xAxis: {
        type: "category",
        data: groups.map(([, label]) => label),
      },
      yAxis: {
        type: "value",
        minInterval: 1,
      },
      series: [
        {
          name: "Success",
          type: "bar",
          data: groups.map(([key]) => bucketFor(key).success_count),
          itemStyle: { color: "#16A34A" },
        },
        {
          name: "Failed",
          type: "bar",
          data: groups.map(([key]) => bucketFor(key).failed_count),
          itemStyle: { color: "#DC2626" },
        },
      ],
    };

    chart.setOption(option);

    return () => {
      chart.dispose();
    };
  }, [paymentOutcomeData, locked]);

  return (
    <CCard className="border-light">
      <CCardBody style={{ overflowX: "auto" }}>
        <span>Number of Payment Method</span>
        {locked ? (
          <div
            key="pm-locked"
            style={{
              height: "400px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#F8F5F5",
              borderRadius: "5px",
              marginTop: "15px",
            }}
          >
            <i
              className="bi bi-lock-fill"
              style={{ fontSize: "32px", color: "#555E67" }}
            ></i>
            <span
              className="text-muted"
              style={{ margin: "10px 0", textAlign: "center" }}
            >
              Transaction amounts are protected.
              <br />
              Enter your password to view them.
            </span>
            <CButton
              color="primary"
              onClick={onUnlock}
              style={{
                backgroundColor: "#0B4FE4",
                borderRadius: "5px",
                color: "white",
              }}
            >
              <i className="bi bi-unlock" style={{ marginRight: "6px" }}></i>
              Unlock
            </CButton>
          </div>
        ) : (
        <>
        <ul className="nav nav-pills mt-3">
          <li className="nav-item">
            <a
              className={`nav-link ${paymentMethod === 1 ? "active" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePillClick(1);
              }}
            >
              Overview
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link ${paymentMethod === 2 ? "active" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePillClick(2);
              }}
            >
              Credit Card
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link ${paymentMethod === 3 ? "active" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePillClick(3);
              }}
            >
              Online Banking
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link ${paymentMethod === 4 ? "active" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePillClick(4);
              }}
            >
              E-Wallet
            </a>
          </li>
        </ul>
        <div
          ref={dropdownRef}
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "6px",
            borderRadius: "5px",
            width: "340px",
            margin: "10px auto",
            marginBottom: "-20px",
            position: "relative",
          }}
        >
          <CButton
            style={{
              border: "0px solid lightgrey",
              backgroundColor: "#F8F5F5",
              borderRadius: "5px",
              color: "#343333",
              height: "45px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              transition: "background-color 0.3s",
            }}
            color="primary"
            onClick={() => setDropdownVisible(!dropdownVisible)}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#e0e0e0";
              const icon = e.target.querySelector("i");
              if (icon) {
                icon.style.backgroundColor = "#e0e0e0";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#F8F5F5";
              const icon = e.target.querySelector("i");
              if (icon) {
                icon.style.backgroundColor = "#F8F5F5";
              }
            }}
          >
            {formatDateRange(dateRange)}
            <i
              className={`bi bi-chevron-down ${
                dropdownVisible ? "rotate" : ""
              }`}
              style={{ marginLeft: "10px" }}
            ></i>
          </CButton>
          {dropdownVisible && (
            <CCard
              className="border-light"
              style={{
                position: "absolute",
                top: "50px",
                zIndex: 1000,
                width: "400px",
              }}
            >
              <CCardBody style={{ overflowX: "auto" }}>
                <span>Filter By Date Range</span>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    padding: "10px",
                    borderRadius: "5px",
                    width: "100%",
                    margin: "0px auto",
                    position: "relative",
                  }}
                >
                  <Flatpickr
                    options={{ mode: "range", dateFormat: "d/m/Y" }}
                    value={tempDateRange}
                    onChange={handleDateChange}
                    className="form-control"
                    style={{
                      border: "1px solid lightgrey",
                      backgroundColor: "white",
                      color: "#555E67",
                      borderRadius: "10px",
                      width: "100%",
                      height: "45px",
                      transition: "background-color 0.3s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#e0e0e0")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  />
                  <i
                    className={`bi bi-calendar`}
                    style={{
                      position: "absolute",
                      right: "20px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  ></i>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-around",
                    marginTop: "0px",
                  }}
                >
                  <CButton
                    color="secondary"
                    onClick={() =>
                      handleButtonClick(setDateRangeToToday, "today")
                    }
                    style={{
                      border: "1px solid lightgrey",
                      backgroundColor:
                        activeButton === "today" ? "#0B4FE4" : "white",
                      borderRadius: "10px",
                      color: activeButton === "today" ? "white" : "#555E67",
                      width: "30%",
                      height: "45px",
                      cursor: "pointer",
                      transition: "background-color 0.3s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor =
                        activeButton === "today" ? "#0056b3" : "#e0e0e0")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor =
                        activeButton === "today" ? "#007bff" : "white")
                    }
                  >
                    Today
                  </CButton>
                  <CButton
                    color="secondary"
                    onClick={() =>
                      handleButtonClick(setDateRangeToThisWeek, "thisWeek")
                    }
                    style={{
                      border: "1px solid lightgrey",
                      backgroundColor:
                        activeButton === "thisWeek" ? "#0B4FE4" : "white",
                      borderRadius: "10px",
                      color: activeButton === "thisWeek" ? "white" : "#555E67",
                      width: "30%",
                      height: "45px",
                      cursor: "pointer",
                      transition: "background-color 0.3s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor =
                        activeButton === "thisWeek" ? "#0056b3" : "#e0e0e0")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor =
                        activeButton === "thisWeek" ? "#007bff" : "white")
                    }
                  >
                    This Week
                  </CButton>
                  <CButton
                    color="secondary"
                    onClick={() =>
                      handleButtonClick(setDateRangeToThisMonth, "thisMonth")
                    }
                    style={{
                      border: "1px solid lightgrey",
                      backgroundColor:
                        activeButton === "thisMonth" ? "#0B4FE4" : "white",
                      borderRadius: "10px",
                      color: activeButton === "thisMonth" ? "white" : "#555E67",
                      width: "30%",
                      height: "45px",
                      cursor: "pointer",
                      transition: "background-color 0.3s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor =
                        activeButton === "thisMonth" ? "#0056b3" : "#e0e0e0")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor =
                        activeButton === "thisMonth" ? "#007bff" : "white")
                    }
                  >
                    This Month
                  </CButton>
                </div>
                <hr></hr>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "10px",
                  }}
                >
                  <CButton
                    color="secondary"
                    onClick={() => {
                      resetDateRange();
                      setActiveButton(null);
                    }}
                    style={{
                      border: "1px solid #0B4FE4",
                      backgroundColor: "white",
                      borderRadius: "5px",
                      color: "#0B4FE4",
                      width: "45%",
                      height: "50px",
                      cursor: "pointer",
                      transition: "background-color 0.3s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#e0e0e0")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "white")
                    }
                  >
                    Reset
                  </CButton>
                  <CButton
                    color="primary"
                    onClick={applyDateRange}
                    style={{
                      border: "1px solid lightgrey",
                      backgroundColor: "#0B4FE4",
                      borderRadius: "5px",
                      color: "white",
                      width: "45%",
                      height: "50px",
                      cursor: "pointer",
                      transition: "background-color 0.3s",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#083bb3")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = "#0B4FE4")
                    }
                  >
                    Apply
                  </CButton>
                </div>
              </CCardBody>
            </CCard>
          )}
        </div>
        <div
          ref={chartRef}
          style={{ height: "400px", width: "100%", margin: "5px auto" }}
        ></div>
        {paymentMethod !== 1 && (
          <div
            style={{
              textAlign: "center",
              marginTop: "10px",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {paymentMethodData.labels.map((label, index) => (
              <div
                key={index}
                style={{
                  display: "inline-block",
                  margin: "0 10px",
                  cursor: "pointer",
                  maxWidth: "20%",
                }}
                onClick={() => toggleItemVisibility(label)}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "20px",
                    height: "12px",
                    backgroundColor: hiddenItems.includes(label)
                      ? "#ccc"
                      : paymentMethodData.datasets[0].backgroundColor[index],
                    marginRight: "5px",
                    borderRadius: "30%",
                  }}
                ></span>
                <span
                  style={{
                    color: hiddenItems.includes(label) ? "darkgrey" : "inherit",
                    fontSize: "13px",
                  }}
                >
                  {label}
                </span>{" "}
              </div>
            ))}
          </div>
        )}
        <hr style={{ marginTop: "30px" }}></hr>
        <span>Success vs Failed Transactions by Payment Method</span>
        <div
          ref={outcomeChartRef}
          style={{ height: "320px", width: "100%", margin: "5px auto" }}
        ></div>
        </>
        )}
      </CCardBody>
    </CCard>
  );
};

export default PaymentMethodCard;
